import { Component, Input, Output, OnInit, EventEmitter, ViewChild } from "@angular/core";

import { BaseComponent, UserService, StorageService, StorageKeys, ProductService, ProductCategory, ProductSnapshot, EventBusService, HelperService, WindowService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { Router, ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { filter, takeUntil } from "rxjs/operators";
import moment from "moment";
import { FuiContentFilterComponent } from "../fender-ui/content-filter/content-filter.component";
import { RecentOrderComponent } from "../modals";

@Component({
  selector: "myp-shared-products-list",
  templateUrl: "products-list.component.html",
  standalone: false,
})
export class ProductsListComponent extends BaseComponent implements OnInit {
  @Input() displayedSection: any = "software";

  private _selectedCategory: ProductCategory;
  @Input() set selectedCategory(category: ProductCategory) {
    this._selectedCategory = category;
    this.selectCategory(this.selectedCategory);
  }
  get selectedCategory() {
    return this._selectedCategory;
  }
  @Output() listLoaded: EventEmitter<boolean> = new EventEmitter();
  @Output() registerClicked: EventEmitter<boolean> = new EventEmitter();
  @Output() categorySelected: EventEmitter<ProductCategory> = new EventEmitter();
  @ViewChild("fuiContentFilter") fuiContentFilter: FuiContentFilterComponent;
  @ViewChild("sidebarFilterModal") sidebarFilterModal: any;
  public loading: boolean = false;
  public refreshing: boolean = false;
  public noProducts: boolean;

  public products: any;

  public allProducts: Array<any> = [];
  public displayedProducts: Array<any> = [];
  public uncategorizedProducts: Array<any> = [];
  public filterAnnouncement: string = "";

  public filterQuery: string = "";

  public samplePrefix = "https://pae-web.presonusmusic.com/uploads/products/";
  public hasMoreThanOneCategory: boolean = false;

  public showFilterOptions: boolean = true;

  public showingRecentOrderModal: boolean = false;
  constructor(
    public storageService: StorageService,
    public userService: UserService,
    public productService: ProductService,
    public appService: AppService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    public translate: TranslateService,
    public eventBusService: EventBusService,
    public helperService: HelperService,
    private _win: WindowService
  ) {
    super(userService);
  }

  ngOnInit() {
    window.scrollTo(0, 0);
    this.loading = true;
    this.displayedSection = this._activatedRoute.snapshot.params["category"];

    this.productService.products$
      .pipe(
        filter((products) => !!products),
        takeUntil(this.destroy$)
      )
      .subscribe((result: any) => {
        if (!result.products.length) this.showFilterOptions = false;
        this.loading = false;
        this.listLoaded.next(true);
        this.setupProducts(result);
        if (this.productService.productCategories?.length) {
          if (this.productService.productCategories.filter((cat) => cat.count).length > 1) {
            this.hasMoreThanOneCategory = true;
            this.showFilterOptions = true;
          } else {
            this.hasMoreThanOneCategory = false;
            this.showFilterOptions = false;
          }
        }

        let recentlyPurchasedProductStringId = this._activatedRoute.snapshot.queryParams["purchasedProductId"];
        if (recentlyPurchasedProductStringId && !this.productService.productsByStringIDs[recentlyPurchasedProductStringId] && !this.showingRecentOrderModal) {
          this.showingRecentOrderModal = true;
          let modal = this.appService.showModal(RecentOrderComponent, { size: "lg" });
          modal.componentInstance.stringId = recentlyPurchasedProductStringId;
          modal.result.then((result) => {
            this.showingRecentOrderModal = false;
          });
          modal.dismissed.subscribe(() => {
            this.showingRecentOrderModal = false;
          });
        }
        this.filterProducts("");
      });
  }

  setupProducts(result) {
    if (result && result.products.length) {
      this.noProducts = false;
    } else {
      this.noProducts = true;
    }

    this.allProducts = [];
    this.uncategorizedProducts = [];
    this.loading = false;
    this.products = result.products;
    this.products.forEach((prod) => {
      this.allProducts.push(prod);
      if (!prod.metadata?.categories?.length) this.uncategorizedProducts.push(prod);
    });

    this.selectCategory(this.selectedCategory);
    this.sortProducts();
  }

  selectCategory(category: ProductCategory) {
    if (this.allProducts && this.allProducts.length) {
      if (category.string_id == "all") {
        this.displayedProducts = [...this.allProducts];
      } else if (category.string_id == "other") {
        this.displayedProducts = [...this.uncategorizedProducts];
      } else {
        this.displayedProducts = [];
        this.allProducts.forEach((item) => {
          let matching: any;
          if (item.metadata?.categories?.length) {
            if (item.metadata.categories.includes(category.string_id)) {
              matching = [item];
            } else if (category.children?.length) {
              matching = category.children.filter((child) => item.metadata.categories.includes(child.string_id));
            }
          }
          if (matching?.length) this.displayedProducts.push(item);
        });
        if (this.displayedProducts.length == 0) {
          this.selectProductCategory("all");
        }
      }
    }
  }

  selectProductCategory(category) {
    this.categorySelected.next(category);
    this.appService.closeModals();
  }

  public sortProducts() {
    const sorter = (a, b) => {
      if (this.appService.sortBy == "date") {
        let dateA = a.releaseDate || a.aquired;
        let dateB = b.releaseDate || b.aquired;
        if (!dateA && !dateB) {
          return 0; // Both are missing, treat as equal
        } else if (!dateA) {
          return this.appService.sortDirection === "asc" ? -1 : 1; // Missing dateA goes to top if 'asc', to bottom if 'desc'
        } else if (!dateB) {
          return this.appService.sortDirection === "asc" ? 1 : -1; // Missing dateB goes to top if 'asc', to bottom if 'desc'
        }
        if (moment(dateA) < moment(dateB)) {
          return this.appService.sortDirection == "asc" ? -1 : 1;
        } else if (moment(dateA) > moment(dateB)) {
          return this.appService.sortDirection == "asc" ? 1 : -1;
        }
      } else {
        if (a.title.toLowerCase() < b.title.toLowerCase()) {
          return this.appService.sortDirection == "asc" ? -1 : 1;
        } else if (a.title.toLowerCase() > b.title.toLowerCase()) {
          return this.appService.sortDirection == "asc" ? 1 : -1;
        }
      }
    };

    this.displayedProducts.sort(sorter);
  }

  downloadInstaller(product) {
    this.productService.getProductSnapshot(product.id).subscribe((snapshot: ProductSnapshot) => {
      if (this.appService.os == "mac") {
        this._getTokenizedInstaller(snapshot.downloads.installers.mac[0]);
      } else {
        if (snapshot.downloads.installers.win64) {
          this._getTokenizedInstaller(snapshot.downloads.installers.win64[0]);
        } else {
          this._getTokenizedInstaller(snapshot.downloads.installers.win[0]);
        }
      }
    });
  }

  private _getTokenizedInstaller(dl) {
    if (dl.tokenized) {
      this.productService
        .getTokenizedUrl(
          dl.folder,
          dl.filename,
          this.userService.appId == "mypresonus" ? "mypresonus" : "studio_one_plus",
          dl.title,
          dl.id.toString(),
          dl.filename,
          parseInt(dl.filesize),
          dl.version || dl.version_number
        )
        .subscribe({
          next: (result: any) => {
            window.open(result.url);
          },
          error: (error) => {
            this.appService.alertError(error);
          },
        });
    } else {
      window.open(dl.url);
    }
  }

  filterProducts(args) {
    const processFn = (item) => {
      let filterBy = item.title.toLowerCase();
      if (!args) {
        item.filtered = false;
      } else if (filterBy.indexOf(args.toLowerCase()) == -1) {
        item.filtered = true;
      } else {
        item.filtered = false;
      }
    };
    this.products.forEach(processFn);

    // Update announcement
    const visibleProducts = this.products.filter((p) => !p.filtered).length;
    if (args) {
      this.filterAnnouncement = visibleProducts > 0 ? `${visibleProducts} results available` : "No results available";
    } else {
      this.filterAnnouncement = "";
    }
  }

  playSample(prod, event?: MouseEvent) {
    const button = (event?.target as HTMLElement).closest("button");
    if (button?.hasAttribute("data-focus-restored")) {
      button.removeAttribute("data-focus-restored");
      return;
    }
    this.eventBusService.emit(this.eventBusService.types.playAudio, {
      url: this.samplePrefix + prod.featuredSample,
      filename: prod.title,
      triggerElement: button,
    });
  }

  tileClick(args, prod) {
    let className = args.srcElement.className.baseVal ? args.srcElement.className.baseVal : args.srcElement.className;
    if (!args.srcElement.classList.contains("tile-action-btn")) {
      this._router.navigate(["products/detail/", prod.id]);
    }
  }

  go(prod) {
    this._router.navigate(["products/detail/", prod.id]);
  }

  upgrade() {
    this._router.navigate(["/onboarding"], { queryParams: { subscribeOnly: true } });
  }

  refresh() {
    this.refreshing = true;
    this.storageService.removeItem(StorageKeys.PRODUCTDETAILSMAP);
    this.storageService.removeItem(StorageKeys.PRODUCTS);
    this.productService.getProducts(true).subscribe((result) => {
      this.refreshing = false;
    });
  }

  clearFilter(event?: any) {
    // Reset to show all products
    this.filterQuery = "";
    this.filterProducts("");
  }

  filter() {
    // Apply current filters
    this.filterProducts(this.filterQuery);
  }
}
